import Layout from "@/components/Layout";
import PageHead from "@/components/PageHead";
import { Mail, MapPin, Phone } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      const { error } = await supabase.from("contact_submissions").insert({
        name: form.name.trim(),
        email: form.email.trim(),
        company: form.company.trim() || null,
        message: form.message.trim(),
      });
      if (error) throw error;
      toast.success("Your inquiry has been submitted. We will respond promptly.");
      setForm({ name: "", email: "", company: "", message: "" });
    } catch (err: any) {
      toast.error("Failed to submit. Please try again or email info@battslegal.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <PageHead title="Contact" description="Contact Batts Legal LLP in Washington, D.C. for strategic antitrust counsel on mergers, FTC/DOJ investigations, litigation, and competition policy." path="/contact" keywords="contact Alicia Batts, antitrust lawyer contact, Washington DC antitrust firm, Batts Legal contact, competition law consultation" />
      <section className="bg-hero text-primary-foreground py-20">
        <div className="editorial-container">
          <div className="gold-accent-line mb-6" />
          <h1 className="text-4xl md:text-5xl font-serif font-semibold mb-4">Contact</h1>
          <p className="text-lg opacity-80 max-w-2xl">Engage Batts Legal LLP for strategic antitrust counsel.</p>
        </div>
      </section>

      <section className="section-padding">
        <div className="editorial-container">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <h2 className="font-serif text-3xl font-semibold text-foreground mb-8">Get in Touch</h2>
              <div className="space-y-6 mb-10">
                <div className="flex items-start gap-4">
                  <MapPin className="w-5 h-5 text-gold mt-1" />
                  <div>
                    <p className="font-medium text-foreground">Office</p>
                    <p className="text-sm text-muted-foreground">Washington, D.C.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Mail className="w-5 h-5 text-gold mt-1" />
                  <div>
                    <p className="font-medium text-foreground">Email</p>
                    <p className="text-sm text-muted-foreground">info@battslegal.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <Phone className="w-5 h-5 text-gold mt-1" />
                  <div>
                    <p className="font-medium text-foreground">Phone</p>
                    <p className="text-sm text-muted-foreground">(202) 596-6160</p>
                  </div>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                For urgent antitrust matters requiring immediate attention, please indicate the nature of the matter in your message. Batts Legal LLP responds promptly to time-sensitive competition law issues.
              </p>
            </div>

            <div className="bg-card rounded-lg p-8 shadow-elevated border border-border">
              <h3 className="font-serif text-xl font-semibold text-foreground mb-6">Send a Message</h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Name *</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="w-full border border-border rounded-sm px-4 py-2.5 text-sm bg-background text-foreground" />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Email *</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="w-full border border-border rounded-sm px-4 py-2.5 text-sm bg-background text-foreground" />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Company</label>
                  <input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="w-full border border-border rounded-sm px-4 py-2.5 text-sm bg-background text-foreground" />
                </div>
                <div>
                  <label className="text-sm font-medium text-foreground block mb-2">Message *</label>
                  <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required className="w-full border border-border rounded-sm px-4 py-2.5 text-sm bg-background text-foreground resize-none" />
                </div>
                <button type="submit" disabled={submitting} className="w-full bg-primary text-primary-foreground py-3 rounded-sm font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50">
                  {submitting ? "Submitting..." : "Submit Inquiry"}
                </button>
                <p className="text-xs text-muted-foreground">This form is for general inquiries. Submitting this form does not create an attorney-client relationship.</p>
              </form>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
